import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  DollarSign, 
  ExternalLink, 
  AlertTriangle, 
  CheckCircle, 
  Filter,
  Info,
  Bell
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { PayrollService } from '../../services/PayrollService';
import { PayrollPeriod } from '../../@types/payroll';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${ThemeColors.background.default};
`;

const MainContent = styled.div`
  padding: ${Spacing.lg};
  padding-top: 100px;
  max-width: 100%;
`;

const PageHeader = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const PageSubtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin-bottom: 0;
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.md};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.md};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacing.md};
  
  svg {
    color: white;
    width: 20px;
    height: 20px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xs};
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const SectionCard = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  margin-bottom: ${Spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding-bottom: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background: white;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const ScheduleItem = styled.div<{ status?: string }>`
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  transition: ${Transitions.duration.standard} ${Transitions.easing.easeInOut};
  
  ${props => props.status === 'current' && `
    border-left: 4px solid ${ThemeColors.primary.main};
    background: linear-gradient(90deg, ${ThemeColors.primary.light}10, transparent);
  `}
  
  ${props => props.status === 'overdue' && `
    border-left: 4px solid ${ThemeColors.error.main};
    background: linear-gradient(90deg, ${ThemeColors.error.light}10, transparent);
  `}
`;

const ScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm};
`;

const PeriodNumber = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${ThemeColors.text.primary};
  
  ${props => {
    switch (props.status) {
      case 'current':
        return `
          background: ${ThemeColors.primary.light};
        `;
      case 'upcoming':
        return `
          background: ${ThemeColors.warning.light};
        `;
      case 'completed':
        return `
          background: ${ThemeColors.success.light};
        `;
      case 'overdue':
        return `
          background: ${ThemeColors.error.light};
        `;
      default:
        return `
          background: ${ThemeColors.gray[100]};
        `;
    }
  }}
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
`;

const DateItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateLabel = styled.span`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs};
`;

const DateValue = styled.span`
  color: ${ThemeColors.text.primary};
  font-weight: ${Typography.fontWeight.medium};
`;

const ActionCard = styled.a`
  display: block;
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  text-decoration: none;
  color: inherit;
  transition: ${Transitions.duration.standard} ${Transitions.easing.easeInOut};
  margin-bottom: ${Spacing.md};
  
  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateY(-2px);
    text-decoration: none;
    color: inherit;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.md};
`;

const ActionIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.md};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacing.md};
  
  svg {
    color: white;
    width: 20px;
    height: 20px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  display: flex;
  align-items: center;
`;

const ActionDescription = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const ExternalLinkIcon = styled(ExternalLink)`
  width: 14px;
  height: 14px;
  color: ${ThemeColors.text.secondary};
  margin-left: ${Spacing.xs};
`;

const NotesSection = styled.div`
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  margin-top: ${Spacing.lg};
`;

const NotesTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const NotesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NotesItem = styled.li`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.sm};
  padding-left: ${Spacing.md};
  position: relative;
  line-height: 1.5;
  
  &::before {
    content: '•';
    color: ${ThemeColors.primary.main};
    font-weight: bold;
    position: absolute;
    left: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xl};
  flex-direction: column;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${ThemeColors.border.light};
  border-top: 4px solid ${ThemeColors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: ${Spacing.md};
  color: ${ThemeColors.text.secondary};
`;

export const PayrollScreenMobile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPayrollPeriods = async () => {
      try {
        setLoading(true);
        setError(null);
        const periods = await PayrollService.getPayrollPeriods();
        setPayrollPeriods(periods);
      } catch (err: any) {
        console.error('[PayrollScreenMobile] Error loading payroll periods:', err);
        setError(err.message || 'Failed to load payroll periods');
      } finally {
        setLoading(false);
      }
    };
    loadPayrollPeriods();
  }, []);

  const filteredSchedule = useMemo(() => {
    if (filter === 'all') return payrollPeriods;
    return payrollPeriods.filter(period => period.status === filter);
  }, [payrollPeriods, filter]);

  const currentPeriod = useMemo(() => {
    return payrollPeriods.find(period => period.status === 'current');
  }, [payrollPeriods]);

  const displayPeriod = useMemo(() => {
    if (currentPeriod) return currentPeriod;
    const overdue = payrollPeriods.find(p => p.status === 'overdue');
    if (overdue) return overdue;
    const upcoming = payrollPeriods.find(p => p.status === 'upcoming');
    return upcoming ?? null;
  }, [payrollPeriods, currentPeriod]);

  const nextPayDate = useMemo(() => displayPeriod?.payDate ?? null, [displayPeriod]);

  const upcomingDeadlines = useMemo(() => {
    return payrollPeriods.filter(period =>
      period.status === 'current' || period.status === 'upcoming'
    ).slice(0, 3);
  }, [payrollPeriods]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'current':
        return <Clock size={14} />;
      case 'upcoming':
        return <Calendar size={14} />;
      case 'completed':
        return <CheckCircle size={14} />;
      case 'overdue':
        return <AlertTriangle size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'current':
        return t('payroll.currentPayPeriod');
      case 'upcoming':
        return t('payroll.upcoming');
      case 'completed':
        return t('payroll.completed');
      case 'overdue':
        return t('payroll.overdue');
      default:
        return status;
    }
  };

  const renderLayout = (children: React.ReactNode) => (
    <PageWrapper>
      {children}
    </PageWrapper>
  );

  if (loading) {
    return renderLayout(
      <MainContent>
        <LoadingSpinner>
          <Spinner />
          <LoadingText>{t('payroll.loading')}</LoadingText>
        </LoadingSpinner>
      </MainContent>
    );
  }

  if (error) {
    return renderLayout(
      <MainContent>
        <PageHeader>
          <PageTitle>{t('payroll.title')}</PageTitle>
          <PageSubtitle style={{ color: ThemeColors.error.main }}>{error}</PageSubtitle>
        </PageHeader>
      </MainContent>
    );
  }

  return renderLayout(
    <MainContent>
      <PageHeader>
        <PageTitle>{t('payroll.title')}</PageTitle>
        <PageSubtitle>{t('payroll.subtitle')}</PageSubtitle>
      </PageHeader>

          {/* Stats Cards - same data logic as desktop */}
          <StatsContainer>
            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.primary.main}>
                  <Calendar />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.currentPayPeriod')}</StatLabel>
                  <StatValue>Period #{displayPeriod?.periodNumber ?? 'N/A'}</StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.success.main}>
                  <DollarSign />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.nextPayDate')}</StatLabel>
                  <StatValue>{nextPayDate ? formatDate(nextPayDate) : 'N/A'}</StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.warning.main}>
                  <Clock />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.submitDeadline')}</StatLabel>
                  <StatValue>
                    {displayPeriod ? formatDate(displayPeriod.submitDate) : 'N/A'}
                    {displayPeriod?.daysRemaining != null && (
                      <span style={{ fontSize: '12px', color: ThemeColors.text.secondary, marginLeft: '8px' }}>
                        {displayPeriod.daysRemaining >= 0
                          ? `(${displayPeriod.daysRemaining} ${t('payroll.daysRemaining')})`
                          : `(${t('payroll.pastDeadline', 'Past deadline')})`}
                      </span>
                    )}
                  </StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>
          </StatsContainer>

          {/* Quick Actions */}
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <Bell />
                {t('payroll.quickActions')}
              </SectionTitle>
            </SectionHeader>

            <ActionCard href="https://www.concursolutions.com/" target="_blank" rel="noopener noreferrer">
              <ActionHeader>
                <ActionIcon color="#0070f3">
                  <img src="/assets/sap-concur-logo.svg" alt="SAP Concur" style={{ width: '20px', height: 'auto' }} />
                </ActionIcon>
                <ActionContent>
                  <ActionTitle>
                    {t('payroll.expenseReports')}
                    <ExternalLinkIcon />
                  </ActionTitle>
                  <ActionDescription>{t('payroll.accessExpenseReports')}</ActionDescription>
                </ActionContent>
              </ActionHeader>
            </ActionCard>

            <ActionCard href="https://online.adp.com/signin/v1/?APPID=WFNPortal&productId=80e309c3-7085-bae1-e053-3505430b5495&returnURL=https://workforcenow.adp.com/&callingAppId=WFN" target="_blank" rel="noopener noreferrer">
              <ActionHeader>
                <ActionIcon color="#ff6b35">
                  <img src="/assets/adp-logo.svg" alt="ADP" style={{ width: '20px', height: 'auto' }} />
                </ActionIcon>
                <ActionContent>
                  <ActionTitle>
                    {t('payroll.salaryAccess')}
                    <ExternalLinkIcon />
                  </ActionTitle>
                  <ActionDescription>{t('payroll.accessSalaryInfo')}</ActionDescription>
                </ActionContent>
              </ActionHeader>
            </ActionCard>
          </SectionCard>

          {/* Payroll Schedule */}
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <Calendar />
                {t('payroll.payrollSchedule')}
              </SectionTitle>
              <FilterContainer>
                <Filter size={16} />
                <FilterSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">{t('payroll.allPeriods')}</option>
                  <option value="current">{t('payroll.currentPayPeriod')}</option>
                  <option value="upcoming">{t('payroll.upcoming')}</option>
                  <option value="completed">{t('payroll.completed')}</option>
                </FilterSelect>
              </FilterContainer>
            </SectionHeader>

            <ScheduleList>
              {filteredSchedule.map((period) => {
                const status = period.status ?? 'upcoming';
                return (
                <ScheduleItem key={period.id} status={status}>
                  <ScheduleHeader>
                    <PeriodNumber>Period #{period.periodNumber}</PeriodNumber>
                    <StatusBadge status={status}>
                      {getStatusIcon(status)}
                      {getStatusText(status)}
                    </StatusBadge>
                  </ScheduleHeader>
                  
                  <DateGrid>
                    <DateItem>
                      <DateLabel>{t('payroll.startDate')}</DateLabel>
                      <DateValue>{formatDate(period.startDate)}</DateValue>
                    </DateItem>
                    <DateItem>
                      <DateLabel>{t('payroll.endDate')}</DateLabel>
                      <DateValue>{formatDate(period.endDate)}</DateValue>
                    </DateItem>
                    <DateItem>
                      <DateLabel>{t('payroll.submitDate')}</DateLabel>
                      <DateValue>{formatDate(period.submitDate)}</DateValue>
                    </DateItem>
                    <DateItem>
                      <DateLabel>{t('payroll.payDate')}</DateLabel>
                      <DateValue>{formatDate(period.payDate)}</DateValue>
                    </DateItem>
                  </DateGrid>
                </ScheduleItem>
                );
              })}
            </ScheduleList>
          </SectionCard>

          {/* Upcoming Deadlines */}
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <Clock />
                {t('payroll.upcomingDeadlines')}
              </SectionTitle>
            </SectionHeader>

            {upcomingDeadlines.map((deadline) => {
              const status = deadline.status ?? 'upcoming';
              return (
              <div key={deadline.id} style={{ 
                padding: `${Spacing.md} 0`, 
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: ThemeColors.text.primary, fontSize: '14px' }}>
                    Period #{deadline.periodNumber}
                  </div>
                  <div style={{ fontSize: '12px', color: ThemeColors.text.secondary }}>
                    {formatDate(deadline.submitDate)}
                  </div>
                </div>
                <StatusBadge status={status}>
                  {getStatusIcon(status)}
                  {getStatusText(status)}
                </StatusBadge>
              </div>
              );
            })}
          </SectionCard>

          {/* Important Notes */}
          <NotesSection>
            <NotesTitle>
              <Info />
              {t('payroll.importantNotes')}
            </NotesTitle>
            <NotesList>
              <NotesItem>{t('payroll.note1')}</NotesItem>
              <NotesItem>{t('payroll.note2')}</NotesItem>
              <NotesItem>{t('payroll.note3')}</NotesItem>
              <NotesItem>{t('payroll.note4')}</NotesItem>
              <NotesItem>{t('payroll.note5')}</NotesItem>
            </NotesList>
          </NotesSection>
        </MainContent>
  );
};
